<template>
  <div class="tw-p-4 sm:tw-p-6">
    <div class="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
      <div class="tw-text-left">
        <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">Trucks</h3>
        <div class="tw-mt-1 tw-text-sm tw-text-slate-600">Manage your fleet.</div>
      </div>

      <button
        type="button"
        class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
        @click="openCreate"
      >
        Add truck
      </button>
    </div>

    <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50 tw-p-3 sm:tw-p-4">
      <div class="tw-mb-3">
        <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
          <input type="checkbox" class="tw-h-4 tw-w-4" v-model="showActiveOnly" />
          Show active trucks only
        </label>
      </div>

      <div class="tw-overflow-x-auto">
        <table class="tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-left">
          <thead>
            <tr class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Truck #</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">VIN</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">License</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Status</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3"></th>
            </tr>
          </thead>
          <tbody class="tw-divide-y tw-divide-slate-200/70">
            <tr
              v-for="truck in filteredTrucks"
              :key="truck.id"
              class="tw-cursor-pointer hover:tw-bg-slate-50/70"
              @click="openEdit(truck)"
            >
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">{{ truck.truckNumber }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">{{ truck.vinNumber }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">{{ truck.licensePlate || '—' }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm">
                <span
                  class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold"
                  :class="truck.isActive ? 'tw-bg-emerald-100 tw-text-emerald-900' : 'tw-bg-slate-200 tw-text-slate-800'"
                >
                  {{ truck.isActive ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="tw-px-3 tw-py-3 tw-text-right">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-800 hover:tw-bg-white"
                  @click.stop="openEdit(truck)"
                >
                  Edit
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Modal
      v-model:isOpen="isModalOpen"
      modalId="truck-modal"
      :title="modalMode === 'create' ? 'Add Truck' : 'Edit Truck'"
      maxWidth="2xl"
    >
      <template #content="{ close }">
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div>
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Truck Number</label>
                <input
                  v-model="form.truckNumber"
                  type="text"
                  class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                />
              </div>
              <div>
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">License Plate</label>
                <input
                  v-model="form.licensePlate"
                  type="text"
                  class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                />
              </div>
              <div class="md:tw-col-span-2">
                <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">VIN</label>
                <input
                  v-model="form.vinNumber"
                  type="text"
                  class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
                />
              </div>

              <div class="md:tw-col-span-2">
                <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
                  <input type="checkbox" class="tw-h-4 tw-w-4" v-model="form.isOwnerOperator" />
                  Is Owner Operator?
                </label>
              </div>
              <div class="md:tw-col-span-2">
                <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
                  <input type="checkbox" class="tw-h-4 tw-w-4" v-model="form.isActive" />
                  Is Active?
                </label>
              </div>
            </div>

        <div v-if="formError" class="tw-mt-4 tw-text-sm tw-text-rose-600">{{ formError }}</div>

        <div class="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-800 hover:tw-bg-slate-50"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60"
            :disabled="isSaving"
            @click="submit"
          >
            <font-awesome-icon v-if="isSaving" icon="circle-notch" spin class="tw-h-4 tw-w-4" />
            <font-awesome-icon v-else icon="check" class="tw-h-4 tw-w-4" />
            Save
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useApolloClient, useQuery } from '@vue/apollo-composable';
import { useAuthStore } from '@/stores/auth';
import Modal from '@/components/core/Modal/Modal.vue';
import TrucksQuery from '../../gql/drivers/Trucks.gql';
import CreateTruckMutation from '../../gql/drivers/CreateTruck.gql';
import UpdateTruckMutation from '../../gql/drivers/UpdateTruck.gql';

const apollo = useApolloClient();

const { result, refetch } = useQuery(TrucksQuery);

const trucks = computed(() => result.value?.trucks || []);

const showActiveOnly = ref(true);
const filteredTrucks = computed(() => {
  const list = trucks.value || [];
  return showActiveOnly.value ? list.filter((t: any) => t.isActive) : list;
});

const isModalOpen = ref(false);
const modalMode = ref<'create' | 'edit'>('create');
const editingTruckId = ref<number | null>(null);
const isSaving = ref(false);
const formError = ref<string | null>(null);

const form = reactive({
  truckNumber: '',
  licensePlate: '',
  vinNumber: '',
  isOwnerOperator: false,
  isActive: true,
});

function resetForm() {
  form.truckNumber = '';
  form.licensePlate = '';
  form.vinNumber = '';
  form.isOwnerOperator = false;
  form.isActive = true;
  formError.value = null;
}

function openCreate() {
  modalMode.value = 'create';
  editingTruckId.value = null;
  resetForm();
  isModalOpen.value = true;
}

function openEdit(truck: any) {
  modalMode.value = 'edit';
  editingTruckId.value = parseInt(String(truck.id), 10);
  form.truckNumber = truck.truckNumber || '';
  form.licensePlate = truck.licensePlate || '';
  form.vinNumber = truck.vinNumber || '';
  form.isOwnerOperator = !!truck.isOwnerOperator;
  form.isActive = truck.isActive !== false;
  formError.value = null;
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
}

function createdTruck() {
  refetch();
}

async function submit() {
  if (isSaving.value) return;
  formError.value = null;

  if (!form.truckNumber || !form.vinNumber) {
    formError.value = 'Truck Number and VIN are required.';
    return;
  }

  isSaving.value = true;
  try {
    if (modalMode.value === 'create') {
      await apollo.client.mutate({
        mutation: CreateTruckMutation,
        variables: {
          truckNumber: form.truckNumber,
          vin: form.vinNumber,
          licensePlate: form.licensePlate,
        },
      });
    } else {
      await apollo.client.mutate({
        mutation: UpdateTruckMutation,
        variables: {
          id: editingTruckId.value,
          truckNumber: form.truckNumber,
          vinNumber: form.vinNumber,
          licensePlate: form.licensePlate,
          isOwnerOperator: form.isOwnerOperator,
          isActive: form.isActive,
        },
      });
    }

    await refetch();
    closeModal();
  } catch (e: any) {
    formError.value = e?.message || String(e);
  } finally {
    isSaving.value = false;
  }
}
</script>
